import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Spin } from 'antd';
import styles from 'LearningManager/styles/UserDashboard.module.scss';
import HeaderRow from 'Pages/TenantProvider/components/HeaderRow';
import OverviewCard from 'LearningManager/components/OverviewCard';
import TrainingSection from 'LearningManager/components/TrainingSection';
import { OVERVIEW_CARDS_DATA } from 'LearningManager/constants/UserDashboard.constants';
import { useStores } from 'Hooks/useStore';
import { ActivityAssignmentFilters } from 'Services/Api/UserDashboard/Types';
import { Status } from 'app-types';
import { DirectoryRole } from 'Services/Api/Directory/Types';

const UserDashboard: React.FC = observer(() => {
	const { t } = useTranslation();
	const { userDashboardStore, learningManagerStore, authStore } = useStores();
	const { activityAssignments, status } = userDashboardStore;
	const categoriesPending =
		learningManagerStore.status.listActivityTypes === Status.Pending;
	const isUserReady = !!authStore.currentUser;

	const [filters, setFilters] = useState<ActivityAssignmentFilters>({});
	const [activityCategories, setActivityCategories] = useState<{
		key: string;
		label: string;
	}[]>([]);
	const [hasLoaded, setHasLoaded] = useState(false);

	useEffect(() => {
		if (!isUserReady) return;
		userDashboardStore.fetchLearningPathAssignments({ offset: 0 });
		if (!learningManagerStore.activityTypes.length) {
			learningManagerStore.fetchActivityTypes().catch(() => undefined);
		}
	}, [isUserReady]);

	useEffect(() => {
		if (!isUserReady) return;
		userDashboardStore.fetchActivityAssignments({ offset: 0, ...filters });
	}, [isUserReady, filters]);

	useEffect(() => {
		if (
			status.listLearningPathAssignments === Status.Success &&
			status.listActivityAssignments === Status.Success &&
			!categoriesPending
		) {
			setHasLoaded(true);
		}
	}, [
		status.listLearningPathAssignments,
		status.listActivityAssignments,
		categoriesPending
	]);

	useEffect(() => {
		(async () => {
			try {
				const response: any = await userDashboardStore.fetchActivitiesCategories();
				const options: DirectoryRole[] = (response?.data?.data || []);
				setActivityCategories(options.map(item => ({ key: String(item.id), label: item.name })))
			} catch (error) {
				setActivityCategories([]);
			}
		})()
	}, [])

	const isLoading =
		!hasLoaded &&
		(!isUserReady ||
			categoriesPending ||
			status.listLearningPathAssignments === Status.Idle ||
			status.listLearningPathAssignments === Status.Pending ||
			status.listActivityAssignments === Status.Idle ||
			status.listActivityAssignments === Status.Pending);

	const overviewCards = useMemo(() => {
		const required = activityAssignments.filter((a) => a.is_required);
		const completedRequired = required.filter(
			(a) => a.status === 'completed'
		);
		const completedAll = activityAssignments.filter(
			(a) => a.status === 'completed'
		);
		const optionalRemaining = activityAssignments.filter(
			(a) => !a.is_required && a.status !== 'completed'
		);

		const overallCompletion = required.length
			? Math.round((completedRequired.length / required.length) * 100)
			: 0;

		const values: Record<string, string | number> = {
			'overall-completion': `${overallCompletion}%`,
			'courses-remaining': required.length - completedRequired.length,
			'courses-complete': completedAll.length,
			'optional-course': optionalRemaining.length
		};

		return OVERVIEW_CARDS_DATA.map((card) => ({
			...card,
			value: values[card.id] ?? card.value
		}));
	}, [activityAssignments]);

	return (
		<div>
			<HeaderRow
				title={t('learning.myCourses.title')}
				subHeading={t('learning.myCourses.subHeading')}
				parentClass={styles.header}
			/>
			{isLoading ? (
				<div className={styles.loading}>
					<Spin size="large" />
				</div>
			) : (
				<>
					<div className={styles['overview-section']}>
						{overviewCards.map((card) => (
							<OverviewCard
								key={card.id}
								value={card.value}
								label={t(card.label)}
								description={card.description ? t(card.description) : ''}
								icon={card.icon}
								tooltip={t(card.tooltip)}
							/>
						))}
					</div>
					<div className={styles['training-section']}>
						<TrainingSection
							activityCategories={activityCategories}
							filters={filters}
							onFiltersChange={setFilters}
						/>
					</div>
				</>
			)}
		</div>
	);
});

export default UserDashboard;
